import { useState } from "react"
import { Chip } from "@/components/Molecules/Chip/Chip"
import { Icon } from "@/components/Atoms/Icon"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer"
import { Button } from "@/components/ui/button"

export type StatusFilter = "all" | "ativos" | "expirados"

export interface ReprisesFilterProps {
  searchTerm: string
  onSearchChange: (value: string) => void
  statusFilter: StatusFilter
  onStatusFilterChange: (value: StatusFilter) => void
  isAdmin: boolean
}

const STATUS_LABELS: Record<string, string> = {
  ativos: "Ativos",
  expirados: "Expirados",
}

export const ReprisesFilter: React.FC<ReprisesFilterProps> = ({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  isAdmin,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [draftStatus, setDraftStatus] = useState<StatusFilter>("all")

  const hasActiveFilters = isAdmin && statusFilter !== "all"

  const openDrawer = () => {
    setDraftStatus(statusFilter)
    setDrawerOpen(true)
  }

  const applyFilters = () => {
    onStatusFilterChange(draftStatus)
    setDrawerOpen(false)
  }

  const clearFilters = () => {
    onStatusFilterChange("all")
    setDraftStatus("all")
    setDrawerOpen(false)
  }

  const activeChips: { key: string; label: string; onRemove: () => void }[] = []

  if (isAdmin && statusFilter !== "all") {
    activeChips.push({
      key: "status",
      label: STATUS_LABELS[statusFilter] ?? statusFilter,
      onRemove: () => onStatusFilterChange("all"),
    })
  }

  return (
    <div>
      {/* Mobile */}
      <div className="lg:hidden">
        <div className="flex items-center gap-3">
          <div className="relative flex flex-1 items-center gap-3 rounded-lg border border-border bg-black/40 px-4 text-white">
            <Icon size={14} name="search" />
            <input
              type="text"
              placeholder="Pesquisar..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full bg-transparent py-2.5 focus:outline-none"
            />
          </div>
          {isAdmin && (
            <button
              onClick={openDrawer}
              className="relative flex size-10 shrink-0 items-center justify-center rounded-lg border border-border bg-black/40"
            >
              <i
                className={`fi fi-ts-sliders-v flex items-center justify-center text-base ${hasActiveFilters ? "text-orange-500" : "text-white"}`}
              />
              {hasActiveFilters && (
                <span className="absolute -right-1 -top-1 size-3 rounded-full bg-orange-500" />
              )}
            </button>
          )}
        </div>

        {activeChips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {activeChips.map((chip) => (
              <Chip key={chip.key} label={chip.label} onClose={chip.onRemove} />
            ))}
          </div>
        )}
      </div>

      {/* Desktop */}
      <div className="hidden lg:block">
        <div className="flex items-center gap-4">
          <div className="relative flex w-80 items-center gap-4 rounded-lg border border-border bg-black/40 px-4 text-white">
            <Icon size={14} name="search" />
            <input
              type="text"
              placeholder="Pesquisar..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full bg-transparent py-2 focus:outline-none"
            />
          </div>

          {isAdmin && (
            <Select
              onValueChange={(v) => onStatusFilterChange(v as StatusFilter)}
              value={statusFilter}
            >
              <SelectTrigger
                className={`w-40 border-border bg-surface ${statusFilter !== "all" ? "border-orange-500 bg-orange-500/10 text-orange-500" : ""}`}
              >
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent className="border-border bg-surface">
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="ativos">Ativos</SelectItem>
                <SelectItem value="expirados">Expirados</SelectItem>
              </SelectContent>
            </Select>
          )}

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="whitespace-nowrap text-sm text-muted-foreground transition-colors hover:text-orange-500"
            >
              Limpar filtros
            </button>
          )}
        </div>

        {activeChips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {activeChips.map((chip) => (
              <Chip key={chip.key} label={chip.label} onClose={chip.onRemove} />
            ))}
          </div>
        )}
      </div>

      {/* Mobile bottom sheet */}
      {isAdmin && (
        <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
          <DrawerContent className="border-white/10 bg-surface px-4 pb-2">
            <DrawerHeader>
              <DrawerTitle className="text-white">Filtros</DrawerTitle>
            </DrawerHeader>

            <div className="flex flex-col gap-4 px-4 pb-2">
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-muted-foreground">Status</span>
                <Select
                  onValueChange={(v) => setDraftStatus(v as StatusFilter)}
                  value={draftStatus}
                >
                  <SelectTrigger
                    className={`border-border bg-black/40 ${draftStatus !== "all" ? "border-orange-500 bg-orange-500/10 text-orange-500" : "text-white"}`}
                  >
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent className="border-border bg-surface">
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="ativos">Ativos</SelectItem>
                    <SelectItem value="expirados">Expirados</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DrawerFooter>
              <Button
                onClick={applyFilters}
                className="w-full bg-orange-500 font-semibold text-white hover:bg-orange-600"
              >
                Aplicar filtro
              </Button>
              <Button
                onClick={clearFilters}
                variant="ghost"
                className="w-full text-muted-foreground hover:text-white"
              >
                Limpar filtro
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      )}
    </div>
  )
}
